import { useEffect, useMemo, useState } from 'preact/hooks';
import { fetchHowToVideos, fetchHowToLiveStatus } from './api.js';
import { HowToCategoryHeader } from './HowToCategoryHeader.jsx';
import { groupHowToVideosByCategory, groupVideosIntoGridRows } from './howToVideoGrouping.js';
import { YouTubeEmbed } from './YouTubeEmbed.jsx';

const GRID_COLUMNS = 3;

function readInitialTab() {
  try {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    return tab === 'live' ? 'live' : 'videos';
  } catch {
    return 'videos';
  }
}

function HowToLivePanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const live = await fetchHowToLiveStatus();
        if (!cancelled) setStatus(live);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Canlı yayın bilgisi alınamadı.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div class="how-to-state">
        <div class="how-to-state__spinner" aria-hidden="true" />
        <p class="how-to-state__text">Yükleniyor…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div class="how-to-state how-to-state--error">
        <div class="how-to-state__icon"><i class="fas fa-exclamation-circle" aria-hidden="true" /></div>
        <p class="how-to-state__text">{error}</p>
      </div>
    );
  }
  if (!status?.is_live || !status?.youtube_url) {
    return (
      <div class="how-to-state">
        <div class="how-to-state__icon how-to-state__icon--muted">
          <i class="fas fa-video" aria-hidden="true" />
        </div>
        <h2 class="how-to-state__title">Şu anda canlı yayın yok</h2>
        <p class="how-to-state__text">Yeni bir yayın başladığında burada görünecek.</p>
      </div>
    );
  }

  return (
    <div class="how-to-live-panel">
      <div class="how-to-live-panel__player">
        <YouTubeEmbed
          videoId={status.youtube_video_id}
          title={status.title || 'Canlı yayın'}
          autoplay
          className="how-to-embed"
        />
      </div>
      <div class="how-to-live-panel__meta">
        <span class="how-to-live-panel__badge">CANLI</span>
        <h2 class="how-to-live-panel__title">{status.title || 'Canlı yayın'}</h2>
      </div>
    </div>
  );
}

function VideoThumb({ video, size = 'grid' }) {
  return (
    <div class={`how-to-thumb how-to-thumb--${size}`}>
      <img
        class="how-to-thumb__img"
        src={video.thumbnail_url}
        alt=""
        loading="lazy"
      />
      <span class="how-to-thumb__play" aria-hidden="true">
        <i class="fas fa-play" />
      </span>
    </div>
  );
}

function HowToVideoGrid({ videos, onPressVideo }) {
  const rows = useMemo(
    () => groupVideosIntoGridRows(groupHowToVideosByCategory(videos), GRID_COLUMNS),
    [videos],
  );

  return (
    <div class="how-to-grid-wrap">
      {rows.map((item, index) => {
        if (item.type === 'header') {
          return (
            <HowToCategoryHeader
              key={item.key}
              categoryKey={item.categoryKey}
              title={item.title}
              videoCount={item.videoCount}
              isFirst={index === 0}
            />
          );
        }

        return (
          <div key={item.key} class="how-to-grid">
            {item.videos.map((video) => (
              <button
                key={video.id}
                type="button"
                class="how-to-grid__item"
                onClick={() => onPressVideo(video)}
              >
                <VideoThumb video={video} size="grid" />
                <span class="how-to-grid__title">{video.title}</span>
              </button>
            ))}
            {item.videos.length < GRID_COLUMNS
              ? Array.from({ length: GRID_COLUMNS - item.videos.length }).map((_, i) => (
                  <div key={`spacer-${item.key}-${i}`} class="how-to-grid__spacer" aria-hidden="true" />
                ))
              : null}
          </div>
        );
      })}
    </div>
  );
}

function HowToVideoList({ videos, onPressVideo }) {
  const sections = useMemo(() => groupHowToVideosByCategory(videos), [videos]);

  return (
    <div class="how-to-list-wrap">
      {sections.map((section, sectionIndex) => (
        <div key={section.key} class="how-to-list-section">
          <HowToCategoryHeader
            categoryKey={section.key}
            title={section.title}
            videoCount={section.videos.length}
            isFirst={sectionIndex === 0}
          />
          <div class="how-to-list">
            {section.videos.map((video) => (
              <button
                key={video.id}
                type="button"
                class="how-to-list__item"
                onClick={() => onPressVideo(video)}
              >
                <VideoThumb video={video} size="list" />
                <span class="how-to-list__body">
                  <span class="how-to-list__title">{video.title}</span>
                  {video.description ? (
                    <span class="how-to-list__desc">{video.description}</span>
                  ) : null}
                </span>
                <span class="how-to-list__chevron" aria-hidden="true">
                  <i class="fas fa-chevron-right" />
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function HowToVideosPage() {
  const initialTab = useMemo(() => readInitialTab(), []);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [viewMode, setViewMode] = useState('grid');
  const [isLive, setIsLive] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchHowToVideos();
        if (!cancelled) setVideos(items);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Videolar yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const live = await fetchHowToLiveStatus();
        if (!cancelled) setIsLive(Boolean(live?.is_live));
      } catch {
        if (!cancelled) setIsLive(false);
      }
    };
    void refresh();
    const timer = setInterval(refresh, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const tab = activeTab === 'live' ? 'live' : 'videos';
    const url = new URL(window.location.href);
    if (tab === 'live') url.searchParams.set('tab', 'live');
    else url.searchParams.delete('tab');
    window.history.replaceState({}, '', url);
  }, [activeTab]);

  useEffect(() => {
    if (!activeVideo) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setActiveVideo(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [activeVideo]);

  const reloadVideos = async () => {
    setLoading(true);
    setError('');
    try {
      const items = await fetchHowToVideos();
      setVideos(items);
    } catch (err) {
      setError(err?.message || 'Videolar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div class="how-to-page">
      <div class="how-to-page__hero">
        <div class="how-to-page__hero-inner">
          <div class="how-to-page__hero-text">
            <h1 class="how-to-page__title">Nasıl Yapılır</h1>
            <p class="how-to-page__subtitle">Kısa eğitim videoları ile ProParcel özelliklerini adım adım öğrenin.</p>
          </div>
        </div>
      </div>

      <div class="how-to-page__body">
        <div class="how-to-toolbar">
          <div class="how-to-tabs" role="tablist" aria-label="Nasıl yapılır sekmeleri">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'videos'}
              class={`how-to-tabs__btn${activeTab === 'videos' ? ' how-to-tabs__btn--active' : ''}`}
              onClick={() => setActiveTab('videos')}
            >
              <i class="fas fa-circle-play" aria-hidden="true" />
              Videolar
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'live'}
              class={`how-to-tabs__btn${activeTab === 'live' ? ' how-to-tabs__btn--active' : ''}`}
              onClick={() => setActiveTab('live')}
            >
              <i class="fas fa-video" aria-hidden="true" />
              Canlı Yayın
              {isLive ? <span class="how-to-tabs__live-dot" aria-hidden="true" /> : null}
            </button>
          </div>

          {activeTab === 'videos' && !loading && !error && videos.length > 0 ? (
            <div class="how-to-view-toggle" role="group" aria-label="Görünüm">
              <button
                type="button"
                class={`how-to-view-toggle__btn${viewMode === 'grid' ? ' how-to-view-toggle__btn--active' : ''}`}
                aria-pressed={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
                title="Grid görünümü"
              >
                <i class="fas fa-th-large" aria-hidden="true" />
              </button>
              <button
                type="button"
                class={`how-to-view-toggle__btn${viewMode === 'list' ? ' how-to-view-toggle__btn--active' : ''}`}
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                title="Liste görünümü"
              >
                <i class="fas fa-list" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>

        {activeTab === 'live' ? (
          <HowToLivePanel />
        ) : loading ? (
          <div class="how-to-state">
            <div class="how-to-state__spinner" aria-hidden="true" />
            <p class="how-to-state__text">Videolar yükleniyor…</p>
          </div>
        ) : error ? (
          <div class="how-to-state how-to-state--error">
            <div class="how-to-state__icon"><i class="fas fa-exclamation-circle" aria-hidden="true" /></div>
            <p class="how-to-state__text">{error}</p>
            <button type="button" class="how-to-state__retry" onClick={reloadVideos}>
              Tekrar dene
            </button>
          </div>
        ) : videos.length === 0 ? (
          <div class="how-to-state">
            <div class="how-to-state__icon how-to-state__icon--muted">
              <i class="fas fa-video" aria-hidden="true" />
            </div>
            <h2 class="how-to-state__title">Henüz video yok</h2>
            <p class="how-to-state__text">Eğitim videoları eklendiğinde burada görünecek.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <HowToVideoGrid videos={videos} onPressVideo={setActiveVideo} />
        ) : (
          <HowToVideoList videos={videos} onPressVideo={setActiveVideo} />
        )}
      </div>

      {activeVideo ? (
        <div
          class="how-to-modal"
          role="dialog"
          aria-modal="true"
          aria-label={activeVideo.title}
          onClick={() => setActiveVideo(null)}
        >
          <div class="how-to-modal__panel" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              class="how-to-modal__close"
              aria-label="Kapat"
              onClick={() => setActiveVideo(null)}
            >
              <i class="fas fa-times" />
            </button>
            <div class="how-to-modal__player">
              <YouTubeEmbed
                videoId={activeVideo.youtube_video_id}
                title={activeVideo.title}
                autoplay
                className="how-to-embed"
              />
            </div>
            <h2 class="how-to-modal__title">{activeVideo.title}</h2>
            {activeVideo.description ? (
              <p class="how-to-modal__description">{activeVideo.description}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
